// frontend/src/hooks/useCubesVisualizer.js
import { useEffect, useRef } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export default function useCubesVisualizer({
  running = true,
  chaos = false,
  glow = true,
  cubeSpeed = 0.5,
  ballSpeed = 0.5,
  pulseSpeed = 0.5,
  flashRate = 0.3,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // --- Scene ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x111111);

    const camera = new THREE.PerspectiveCamera(60, 900 / 600, 0.1, 1000);
    // Caméra rapprochée pour compenser l'échelle de la géométrie CANONIQUE.
    //
    // Le front dispersait auparavant les cubes sur ±30 avec `Math.random()` ;
    // le générateur du backend les place dans un domaine d'amplitude ~10,6.
    // Sans ajustement, la scène apparaissait environ 3× plus dense.
    //
    // C'est bien la CAMÉRA qu'on déplace, pas la géométrie : élargir le
    // générateur aurait changé les positions mesurées, donc invalidé les
    // rapports serveur/navigateur. Décision produit du 2026-07-28.
    camera.position.set(11, 11, 18);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(900, 600, false);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    const dir = new THREE.DirectionalLight(0xffffff, 1);
    dir.position.set(10, 15, 10);
    scene.add(ambient, dir);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // --- Arrays pour snapshot ---
    const cubeMeshes = [];
    let cubeVelocities = [];

    // ═══════════════════════════════════════════════════════════════════════
    // LE BACKEND POSSÈDE LA FORME ET LE MOUVEMENT
    // docs/DOCTRINE_GEOMETRIE_CANONIQUE.md
    //
    // Ce hook simulait sa propre physique — vitesses tirées au hasard,
    // collisions cube↔cube, rebonds sur des bornes à ±45, confinement des
    // billes — pendant que le backend faisait tourner `update_cubes_dynamics`
    // (gravité, rebonds, confinement, masses). Deux moteurs concurrents sous
    // le même nom : toute comparaison serveur/navigateur comparait deux
    // mouvements différents.
    //
    // Le front rejoue désormais les frames servies par `/animate`.
    // ═══════════════════════════════════════════════════════════════════════
    let lecteur = null;
    let frames = null;
    let frameDt = 0.1;
    let playhead = 0;
    const ballMeshes = [];
    let ballVelocities = [];

    // --- Initial cubes from backend ---
    fetch("/api/geometry/cubes/initial")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!text) throw new Error("Réponse vide du backend");
        return JSON.parse(text);
      })
      .then((data) => {
        if (!data.cubes) throw new Error("Réponse backend invalide");

        data.cubes.forEach((cube) => {
          // Cube filaire semi-transparent
          const geometry = new THREE.BoxGeometry(cube.size, cube.size, cube.size);
          const material = new THREE.MeshPhongMaterial({
            color: cube.color,
            transparent: true,
            opacity: 0.3,
            wireframe: true,
            emissive: new THREE.Color(0x000000),
            emissiveIntensity: 0.0,
          });
          const mesh = new THREE.Mesh(geometry, material);

          // Position CANONIQUE reçue du backend.
          // ⚠️ Elle était auparavant écrasée par `(Math.random()-0.5)*60` : le
          // front demandait la géométrie au backend puis la jetait. Voir
          // docs/DOCTRINE_GEOMETRIE_CANONIQUE.md.
          mesh.position.set(...cube.position);
          mesh.rotation.set(...cube.rotation);

          scene.add(mesh);
          cubeMeshes.push(mesh);

          // Vitesse MESURÉE, dérivée du déplacement réel entre deux frames —
          // plus un tirage `Math.random()` qui partait ensuite dans le snapshot
          // d'entropie comme s'il avait été observé.
          cubeVelocities.push(new THREE.Vector3(0, 0, 0));

          // Billes internes
          cube.balls.forEach((ball) => {
            const sphereGeo = new THREE.SphereGeometry(ball.radius || 0.5, 16, 16);
            const sphereMat = new THREE.MeshPhongMaterial({
              color: new THREE.Color(...ball.color),
            });
            const sphere = new THREE.Mesh(sphereGeo, sphereMat);
            sphere.position.set(...ball.position);

            mesh.add(sphere);
            ballMeshes.push(sphere);

            ballVelocities.push(new THREE.Vector3(0, 0, 0));
          });
        });

        // Le backend possède aussi le MOUVEMENT : on charge la séquence de
        // frames qu'il produit avec son moteur physique (gravité, rebonds,
        // confinement, billes). Le front les rejoue, il ne simule rien.
        //
        // ⚠️ LES CURSEURS SONT TRANSMIS AU MOTEUR, pas appliqués au rejeu.
        // Première version : ils ne pilotaient que la cadence de lecture, ce
        // qui laissait `ballSpeed` SANS AUCUN EFFET — il n'agissait plus sur
        // rien. Un curseur doit agir sur le moteur (docs/DOCTRINE_ENTROPIE_PHYSIQUE.md,
        // règle 2), et le moteur est désormais côté serveur : c'est donc là
        // qu'il doit arriver.
        // ⚠️ LECTURE CONTINUE, plus un segment unique rebouclé.
        //
        // Le front demandait UNE séquence et la rejouait en boucle : à la
        // dernière frame la scène se téléportait vers la première. Mesuré sur
        // ce module : un saut de 40x le pas typique, toutes les 24 secondes.
        //
        // Le lecteur partagé demande le segment suivant AVANT la fin du
        // courant, en présentant le jeton de continuation, et bascule sans
        // rupture. Voir docs/SPEC_CONTINUATION.md.
        lecteur = creerLecteurContinu({
          url: "/api/geometry/cubes/animate",
          parametres: () => ({
            steps: 240,
            dt: 0.1,
            ball_speed: ballSpeed * 2,   // 0..1 -> 0..2
            cube_speed: 0.4 + cubeSpeed * 3.2,   // 0..1 -> 0,4..3,6
            chaos: chaos ? 2.0 : 0.3,
          }),
        });
        lecteur.demarrer();
        return null;
      })
      .catch((err) => {
        console.error("Erreur fetch cubes initial:", err);
      });

    // --- Snapshot pour dyn_entropy ---
    canvas.__oracleSnapshot = () => ({
      cube_positions: cubeMeshes.map((c) => c.position.toArray()),
      cube_colors: cubeMeshes.map((c) => c.material.color.toArray()),
      cube_velocities: cubeVelocities.map((v) => v.toArray()),
      ball_positions: ballMeshes.map((b) => b.position.toArray()),
      ball_colors: ballMeshes.map((b) => b.material.color.toArray()),
      ball_velocities: ballVelocities.map((v) => v.toArray()),
    });

    // --- Animate loop ---
    let prev = performance.now();
    let t = 0;

    function animate() {
      requestAnimationFrame(animate);
      const now = performance.now();
      const dt = Math.min((now - prev) / 1000, 0.05);
      prev = now;
      t += dt;

      if (running) {
        // --- Lecture des frames backend (aucune position recalculée ici) ---
        //
        // ⚠️ LE PLAYHEAD AVANCE AU TEMPS RÉEL. Il était multiplié par
        // `cubeSpeed * 4.0` — le curseur pilotait donc la CADENCE DE LECTURE.
        // Or les vitesses sont MESURÉES plus bas sur le déplacement par `dt`
        // réel : accélérer la lecture multipliait directement les vitesses
        // mesurées, donc la métrique, sans passer par la scène. C'est ce que
        // la règle 2 interdit, et le défaut jumeau de celui corrigé sur
        // `spiral`. Les curseurs physiques (`ball_speed`, `chaos`) sont déjà
        // transmis au moteur, en paramètres de `/animate`.
        let fa = null, fb = null, alpha = 0;
        if (lecteur) {
          playhead += dt;
          const vue = lecteur.lire(playhead);
          if (vue) {
            if (vue.bascule || vue.gel) playhead = 0;   // repère du nouveau segment
            fa = vue.a;
            fb = vue.b;
            alpha = vue.alpha;
          }
        }

        // --- Mvt cubes ---
        cubeMeshes.forEach((mesh, i) => {
          if (fa && fb && fa.cubes[i] && fb.cubes[i]) {
            const a = fa.cubes[i].position;
            const b = fb.cubes[i].position;
            const nx = a[0] + (b[0] - a[0]) * alpha;
            const ny = a[1] + (b[1] - a[1]) * alpha;
            const nz = a[2] + (b[2] - a[2]) * alpha;
            // Vitesse MESURÉE : déplacement réel sur le pas écoulé.
            if (dt > 0) {
              cubeVelocities[i].set(
                (nx - mesh.position.x) / dt,
                (ny - mesh.position.y) / dt,
                (nz - mesh.position.z) / dt
              );
            }
            mesh.position.set(nx, ny, nz);
          }

          // Glow multicolore
          if (glow) {
            const hueShift = (Math.sin(t * 0.5 + i) + 1) / 2;
            mesh.material.color.setHSL(hueShift, 0.6, 0.5);
          }

          // Effet flash pulsant basé sur flashRate
          if (flashRate > 0) {
            const flash = 0.5 + 0.5 * Math.sin(t * 10 * flashRate);
            mesh.material.emissive = new THREE.Color(0x03dac6);
            mesh.material.emissiveIntensity = flash * flashRate * 2.0;
          } else {
            mesh.material.emissiveIntensity = 0;
          }
        });

        // --- Collisions cube ↔ cube : RETIRÉ ---
        // Le moteur backend (`update_cubes_dynamics`) traite déjà les
        // collisions, avec masses et facteur de rebond. Les recalculer ici
        // aurait déplacé les cubes hors des positions servies, donc rompu la
        // correspondance entre ce qui est affiché et ce qui est mesuré.

        // --- Billes : positions rejouées, non simulées ---
        ballMeshes.forEach((ball, i) => {
          if (fa && fb) {
            // Les billes sont ordonnées cube par cube, comme dans la frame.
            const ci = Math.floor(i / (fa.cubes[0]?.balls?.length || 1));
            const bj = i % (fa.cubes[0]?.balls?.length || 1);
            const ba = fa.cubes[ci]?.balls?.[bj]?.position;
            const bb = fb.cubes[ci]?.balls?.[bj]?.position;
            if (ba && bb) {
              const nx = ba[0] + (bb[0] - ba[0]) * alpha;
              const ny = ba[1] + (bb[1] - ba[1]) * alpha;
              const nz = ba[2] + (bb[2] - ba[2]) * alpha;
              if (dt > 0) {
                ballVelocities[i].set(
                  (nx - ball.position.x) / dt,
                  (ny - ball.position.y) / dt,
                  (nz - ball.position.z) / dt
                );
              }
              ball.position.set(nx, ny, nz);
            }
          }
          // Couleur dynamique
          const speed = ballVelocities[i].length();
          const hue = (t * 0.5 + speed * 10) % 1;
          ball.material.color.setHSL(hue, 0.7, 0.5);
        });

        // --- Pulsation fluide ---
        if (pulseSpeed > 0) {
          const scale = 1 + 0.05 * Math.sin(t * 2.0 * pulseSpeed);
          cubeMeshes.forEach((c) => c.scale.set(scale, scale, scale));
        }
      }

      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    return () => {
      // ⚠️ Arrêter le lecteur AVANT de libérer le renderer : il porte une
      // requête de préchargement en vol, qui écrirait dans un état démonté.
      if (lecteur) lecteur.arreter();
      delete canvas.__oracleSnapshot;
      renderer.dispose();
    };
  }, [running, chaos, glow, cubeSpeed, ballSpeed, pulseSpeed, flashRate]);

  return canvasRef;
}
